import React from 'react';
import { Box, IconButton, Stack, Typography } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import CloseIcon from '@mui/icons-material/CloseRounded';
import { isImage, processImage } from '../../../utils/image';
import WaiverImageDropzone from '../WaiverImageDropzone';
import ColoredAlert from '../../global/ColoredAlert';

const THUMB_SIZE = 88;

interface SingleProps {
  multiple: false;
  label: string;
  value: string;
  onChange: (value: string) => void;
}

interface MultipleProps {
  multiple: true;
  label: string;
  value: string[];
  onChange: (value: string[]) => void;
}

type Props = SingleProps | MultipleProps;

export default function WaiverImageInput({
  label,
  multiple,
  value,
  onChange,
}: Props) {
  const inputRef = React.useRef<HTMLInputElement>(null);
  const [isDragging, setIsDragging] = React.useState(false);
  const [error, setError] = React.useState('');

  const images: string[] = multiple
    ? (value as string[])
    : value
    ? [value as string]
    : [];

  const hasImages = images.length > 0;
  const canAddMore = multiple || !hasImages;

  const handleFiles = React.useCallback(
    async (files: FileList | null) => {
      if (!files || files.length === 0) return;
      const imageFiles = Array.from(files).filter(isImage);
      if (imageFiles.length === 0) return;
      try {
        const dataUrls = await Promise.all(imageFiles.map(processImage));
        setError('');
        if (multiple) {
          (onChange as MultipleProps['onChange'])([
            ...(value as string[]),
            ...dataUrls,
          ]);
        } else {
          (onChange as SingleProps['onChange'])(dataUrls[0]);
        }
      } catch (err) {
        setError(
          err instanceof Error ? err.message : 'Failed to process image',
        );
      }
    },
    [multiple, value, onChange],
  );

  const handleRemove = React.useCallback(
    (index: number) => {
      if (multiple) {
        const next = (value as string[]).filter((_, i) => i !== index);
        (onChange as MultipleProps['onChange'])(next);
      } else {
        (onChange as SingleProps['onChange'])('');
      }
    },
    [multiple, value, onChange],
  );

  const handleDragOver = React.useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(true);
  }, []);

  const handleDragLeave = React.useCallback((e: React.DragEvent) => {
    if (e.currentTarget.contains(e.relatedTarget as Node)) return;
    setIsDragging(false);
  }, []);

  const handleDrop = React.useCallback(
    (e: React.DragEvent) => {
      e.preventDefault();
      setIsDragging(false);
      handleFiles(e.dataTransfer.files);
    },
    [handleFiles],
  );

  return (
    <Stack spacing={1}>
      <Typography variant="h5">{label}</Typography>

      {/* Has images — thumbnail grid with inline add tile */}
      {hasImages && (
        <Box
          position="relative"
          onDragOver={canAddMore ? handleDragOver : undefined}
          onDragLeave={canAddMore ? handleDragLeave : undefined}
          onDrop={canAddMore ? handleDrop : undefined}>
          <Stack direction="row" flexWrap="wrap" gap={1}>
            {images.map((src, i) => (
              // eslint-disable-next-line react/no-array-index-key
              <Box key={`img-${i}`} position="relative" flexShrink={0}>
                <Box
                  component="img"
                  src={src}
                  alt={`Upload ${i + 1}`}
                  sx={{
                    width: THUMB_SIZE,
                    height: THUMB_SIZE,
                    objectFit: 'cover',
                    borderRadius: 1.5,
                    display: 'block',
                  }}
                />
                <IconButton
                  size="small"
                  onClick={() => handleRemove(i)}
                  sx={{
                    position: 'absolute',
                    top: -6,
                    right: -6,
                    bgcolor: 'white',
                    boxShadow: '0 1px 4px rgba(0,0,0,0.15)',
                    width: 20,
                    height: 20,
                    p: 0,
                    ':hover': { bgcolor: 'lightOutlineGrey.main' },
                  }}>
                  <CloseIcon sx={{ fontSize: 11 }} />
                </IconButton>
              </Box>
            ))}

            {/* Inline add tile */}
            {canAddMore && (
              <Box
                onClick={() => inputRef.current?.click()}
                sx={theme => ({
                  width: THUMB_SIZE,
                  height: THUMB_SIZE,
                  flexShrink: 0,
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: 0.5,
                  borderRadius: 1.5,
                  border: '1.5px dashed',
                  borderColor: theme.palette.outlineGrey.main,
                  cursor: 'pointer',
                  transition: 'border-color 0.15s, background-color 0.15s',
                  ':hover': {
                    borderColor: theme.palette.primary.main,
                    backgroundColor: `${theme.palette.primary.main}08`,
                    '& .add-icon': { color: theme.palette.primary.main },
                  },
                })}>
                <AddIcon
                  className="add-icon"
                  sx={() => ({
                    fontSize: 20,
                    color: 'text.primary',
                    transition: 'color 0.15s',
                  })}
                />
                <Typography
                  variant="caption"
                  sx={() => ({
                    color: 'text.primary',
                    lineHeight: 1.2,
                    textAlign: 'center',
                  })}>
                  Add more
                </Typography>
              </Box>
            )}
          </Stack>

          {/* Drag overlay */}
          {isDragging && canAddMore && (
            <Box
              sx={theme => ({
                position: 'absolute',
                inset: -4,
                borderRadius: 2,
                border: '2px dashed',
                borderColor: theme.palette.primary.main,
                backgroundColor: `${theme.palette.primary.main}12`,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                pointerEvents: 'none',
              })}>
              <Typography
                variant="h5"
                sx={theme => ({ color: theme.palette.primary.main })}>
                Drop to add
              </Typography>
            </Box>
          )}
        </Box>
      )}

      {/* Empty state — full dropzone */}
      {!hasImages && (
        <WaiverImageDropzone
          multiple={multiple}
          isDragging={isDragging}
          onClick={() => inputRef.current?.click()}
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          onDrop={handleDrop}
        />
      )}

      {error && <ColoredAlert type="error" subtitle={error} dismissible />}

      <input
        ref={inputRef}
        type="file"
        accept="image/jpeg, image/png, image/webp, .jpg, .jpeg, .png, .webp, .heic, .heif"
        multiple={multiple}
        hidden
        onChange={e => handleFiles(e.target.files)}
      />
    </Stack>
  );
}
